import { autoinject, transient, computedFrom, observable, BindingEngine } from "aurelia-framework";
import { DataType, DataGridColumn, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { LookupValue, LookupValueRequest, LookupValueResponse } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import * as Enumerable from "linq";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { Reflection } from "../../../../utilities/reflection";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { BindingSignaler } from "aurelia-templating-resources";
import { TasksConstants } from "../../../../models/tasks/task.model";
import { Guid } from "../../../../utilities/guid";
import KeyDownEvent = JQuery.KeyDownEvent;
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { RightShipInspectionStatusEnum, RightShipInspectionType, RightshipInspectionActivitiesColumns } from "../../../../models/rightship-inspections/rightship-inspections.models";
import { QueriesClientService } from "../../../../services/queries/queries-client.services";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import * as moment from "moment";
import { EntityFinderSearchFilter } from "../../../components/entity-finder-search-filter/entity-finder-search-filter";
import { GetBusinessUnitIdsResponse } from "../../../../models/customers/customer.models";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';

export enum RightShipInspectionFilters {

    // Default Filters
    PendingSubtask = 1,
    PendingVet = 2,

    // Status Filters
    Created = 4,
    InProgress = 8,
    AwaitingResponse = 16,
    InspectionReassignment = 32,
    Acceptable = 64,
    Unacceptable = 128,
    PendingReview = 256,
    Canceled = 512,

    Statuses = Created | InProgress | AwaitingResponse | InspectionReassignment | Acceptable | Unacceptable | PendingReview | Canceled,
}

@autoinject()
@transient()
export class RightShipInspectionActivities extends CustomGridPageBase {

    public customerEntityTypes: EntityTypeConfigurationModel[];
    public portEntityTypes: EntityTypeConfigurationModel[];
    public inspectorEntityTypes: EntityTypeConfigurationModel[];
    isMobileFilterOpen: boolean;
    rightShipInspectionStatuses: LookupValue[];
    public showStatusFilter: boolean = false;
    public showCorrespondencesFilter : boolean = false;
    public defaultPageCount: number = 100;
    @cachable
    public statusFilterIds: number[] = [];
    @cachable
    public correspondencesFilterIds: number[] = [];
    @cachable
    public selectedAdditionalInfoName: string[] = [];
    public mobileStatusOpen: boolean;
    public mobileCorrespondencesOpen: boolean;
    public allRightShipInspectionStatuses: LookupValue[];
    @cachable
    public rightshipMember: boolean = false;
    @cachable
    public awaitingResponseLT20Days: boolean = false; 
    @cachable
    public validityDateModified: boolean = false;
    public rightShipUser: boolean;
    public canViewAwaitingResponseLT20DaysInTasks: boolean;
    public isCustomer: boolean = false;
    public pendingVetCount: number = 0;
    public accessableBusinessUnits: LookupValue[];
    public hasCanViewSurveyColumnInTasksPermission: boolean;
    public shouldShowInspectionFilter: boolean = false;
    public additionalInfos =
        [
            { id: 1, name: "60 days reminder", dbpropertyValue: '60 days reminder' },
            { id: 2, name: "30 days reminder", dbpropertyValue: '30 days reminder' },
            { id: 3, name: "Survey received", dbpropertyValue: 'Survey Received' }
        ];
    public correspondencesFilterItems = 
    [
        { id: 1, name: "in the past 24 hours", dbpropertyValue: 'past24' },
        { id: 2, name: "in the past 48 hours", dbpropertyValue: 'past48' },
        { id: 3, name: "in the past 72 hours", dbpropertyValue: 'past72' },
        { id: 4, name: "> 72 hours", dbpropertyValue: 'morethan72' }
    ];


    @observable()
    @cachable
    filter: string;
    @cachable
    appliedFilter: string;
    initialFilters: QueryFilter[];

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }

    clearFilters(reload = true) {
        if(this.dataGridVM)
        {
            this.dataGridVM.clearSorting();
        }
        this.statusFilterIds = [];
        this.mobileStatusIds = [];
        this.correspondencesFilterIds = [];
        this.myInspectionsFilterSelected = false;
        this.newInspectionFilterSelected = false;
        this.alternativeRequiredFilterSelected = false;
        this.reviewRequiredFilterSelected = false;
        this.selectedBUs = [];
        this.selectedCustomers = [];
        this.selectedInspectionTypes = [];
        this.selectedAdditionalInfo = [];
        this.rightshipMember = false;
        this.validityDateModified = false;
        this.isPendingVet = false;
        this.isPendingDueDiligence = false;
        this.selectedPorts = [];
        this.selectedInspectors = [];
        if (this.portEntityFinderFilter) {
            this.portEntityFinderFilter.clear();
        }
        else {
            this.clearPortOnShow = true;
        }
        if (this.inspectorEntityFinderFilter) {
            this.inspectorEntityFinderFilter.clear();
        }
        else {
            this.clearInspectorOnShow = true;
        }
        this.closedWithoutGradingFilterSelected = false;
        this.unreadMessageFilterSelected = false;
        this.clearFilter(reload);
    }

    clearFilter(reload = true) {
        this.filter = "";
        this.appliedFilter = this.filter;

        if (reload)
            this.reloadFilters();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 3 : 2,
                dataType: DataType.CustomTemplate,
                name: 'submittedDate',
                title: 'Submitted Date',
                isSortable: true,
                isAscending: this.rightShipUser ? null : false,
                isHidden: true,
                secondParameter: 'MMMM Do YYYY',
                value: '<div class="column">' +
                    '<span>${getDateToString(item.submittedDate)}</span>',
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                },
                instancePerRow: true

            },
            {
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusRank',
                title: 'StatusRank'
            },
            {
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 2 : 3,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusDate',
                title: 'StatusDate'
            });
        return columns;
    }

    protected get odataUrl(): string {
        return "odata/RightShipInspections";
    }

    public get rightShipInspectionStatusTypes() {
        return RightShipInspectionStatusEnum;
    }

    public get filterTypes() {
        return RightShipInspectionFilters;
    }

    //#region Status Filters

    @cachable
    public newInspectionFilterSelected: boolean = false;
    @cachable
    public reviewRequiredFilterSelected: boolean = false;
    @cachable
    public alternativeRequiredFilterSelected: boolean = false;
    @cachable
    public closedWithoutGradingFilterSelected: boolean = false;

    public getExcludedStatuses(): number[] {
        return [
            RightShipInspectionStatusEnum.NewInspection,
            RightShipInspectionStatusEnum.ReviewRequired,
            RightShipInspectionStatusEnum.RightShipInspectionReassignment,
            RightShipInspectionStatusEnum.AlternativeRequired
        ];
    }

    public newInspectionClick(isMobile: boolean = false) {
        this.newInspectionFilterSelected = !this.newInspectionFilterSelected;
        if (!isMobile) this.reloadFilters();
    }

    public reviewRequiredClick(isMobile: boolean = false) {
        this.reviewRequiredFilterSelected = !this.reviewRequiredFilterSelected;
        if (!isMobile) this.reloadFilters();
    }

    public alternativeRequiredClick(isMobile: boolean = false) {
        this.alternativeRequiredFilterSelected = !this.alternativeRequiredFilterSelected;
        if (!isMobile) this.reloadFilters();
    }

    closedWithoutGradingClick(isMobile: boolean = false){
        this.closedWithoutGradingFilterSelected = !this.closedWithoutGradingFilterSelected;
        if (!isMobile) this.reloadFilters();
    }
    //#endregion

    @computedFrom(Reflection.getPropertyName<RightShipInspectionActivities>(pa => pa.rightShipInspectionStatuses))
    public get filterNames() {
        const mappings: { [id: number]: string } = {};

        if (this.rightShipInspectionStatuses) {
            this.rightShipInspectionStatuses.forEach(lv => mappings[lv.id] = lv.name);
        }

        return mappings;
    }

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private signalClientService: SignalClientService, private lookupClientService: LookupClientService, private bindingSignaler: BindingSignaler,
        private userClaimsService: UserClaimsService, private queryService: QueriesClientService, private userClientService: UserClientService,
        private commonCustomerService: CommonCustomerService,
        private customerService: CustomerService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        super(i18N, TasksConstants.INSPListName, bindingEngine);
        this.customerEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Customers"]);
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.inspectorEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Inspectors"]);
    }
    
    async init()
    {
        const promises = [];
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAccessDueDiligence = this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence);
        this.shouldShowInspectionFilter = !this.canAccessCargillOnly;
        this.canViewAwaitingResponseLT20DaysInTasks = this.userClaimsService.hasPermission(Claims.CanViewAwaitingResponseLT20DaysInTasks)
        this.hasCanViewSurveyColumnInTasksPermission = this.userClaimsService.hasPermission(Claims.CanViewSurveyColumnInTasks);
        this.columns = RightshipInspectionActivitiesColumns.getColumns(false, false, this.rightShipUser, { showSurvey: this.hasCanViewSurveyColumnInTasksPermission });

        if (!this.rightShipUser) {
            promises.push(this.customerService.getAllAccessableBusinessUnitsByType(EntityTypes.RightShipInspection));
            const request = new LookupValueRequest();
            request.lookupValueQuery = LookupValueQuery.BusinessUnits;

            promises.push(this.lookupClientService.getLookup(request));
        }
        
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RightShipInspectionStatuses;
        promises.push(this.lookupClientService.getLookup(request));
        const handleStatusLookupResult = (x: LookupValueResponse) => {
            const values = x.activeValues;
            this.rightShipInspectionStatuses = Enumerable.from(values).orderBy(x => x.id).where(x => x.id != RightShipInspectionStatusEnum.RightShipInspectionReassignment).toArray();
            this.allRightShipInspectionStatuses = values;
        };

        const handleBULookupResult = (bulookupResult: LookupValueResponse, accessibleBUs: GetBusinessUnitIdsResponse) => {
            this.accessableBusinessUnits = bulookupResult.activeValues.filter(value => accessibleBUs.businessUnitIds.indexOf(value.id) > -1);
        }

        const results = await Promise.all(promises);

        if(!this.rightShipUser)
        {
            handleBULookupResult(results[1], results[0]);
            handleStatusLookupResult(results[2]);
        }
        else
        {
            handleStatusLookupResult(results[0]);
        }

        const customerId = this.userClientService.currentUser.settings.customerId;
        //fallback get customerid
        if (customerId == null || customerId <= 0) {
            try{
                const customerIdResponse = await this.commonCustomerService.getCustomerId();
                this.isCustomer = (customerIdResponse && customerIdResponse > 0);
            }
            catch (e)
            {
                console.warn(e);
            }
        }
        else this.isCustomer = true;

        this.initialSorting = this.getSorting();

        if (this.applyCache()) {
            this.reloadFilters();
        } else {
            this.setDefaultFilters();
        }
    }

    async activate() {
        await this.init();
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showBUFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showCustomerFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showInspectionTypeFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showBillingStatusFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showAdditionalInfoFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showCorrespondencesFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.mobileStatusOpen))
    public get isSubFilterOpen(): boolean {
        return this.showBUFilter ||
            this.showCustomerFilter || this.showInspectionTypeFilter || this.showAdditionalInfoFilter || this.mobileStatusOpen || this.showBillingStatusFilter || this.showCorrespondencesFilter;
    }

    setDefaultFilters() {
        this.newInspectionFilterSelected = true;
        this.reloadFilters();
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showBUFilter = false;
        this.showCustomerFilter = false;
        this.showInspectionTypeFilter = false;
        this.showBillingStatusFilter = false;
        this.showAdditionalInfoFilter = false;
        this.showStatusFilter = false;
        this.mobileStatusOpen = false;
        this.showCorrespondencesFilter = false;
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        super.detached();
    }

    refreshRightShipInspectionStatus = (param: EntityStatusChangeModel) => {
        if (this.dataGridVM) {
            if (this.dataGridVM.page == 0) {
                this.dataGridVM.reloadData();
            } else {
                const item = this.dataGridVM.itemsCollection.filter(x => x.item.rightShipInspectionId == param.entityId)[0];
                if (item) {
                    item.item.statusId = param.statusId;
                    item.item.status = this.rightShipInspectionStatuses.filter(x => x.id == param.statusId).map(x => x.name)[0];
                }
            }
            this.refreshActivityCounts();
        }
    }

    toggleStatusFilters() {
        this.showStatusFilter = !this.showStatusFilter;
    }

    onStatusFilter = (event) => {
        this.statusFilterIds = event.filters.map(x => x.id);
        this.reloadFilters();
        this.showStatusFilter = false;
    }


    @computedFrom('correspondencesFilterIds', 'correspondencesFilterIds.length')
    public get correspondencesFilterCount() {
        return this.correspondencesFilterIds ? this.correspondencesFilterIds.length : 0;
    }

    toggleCorrespondencesFilters() {
        if (!this.showCorrespondencesFilter) {
            this.closeAllFilters();
        }
        this.showCorrespondencesFilter = !this.showCorrespondencesFilter;
    }

    onCorrespondencesFilter = (event) => {
        this.correspondencesFilterIds = event.filters.map(x => x.id);
        this.reloadFilters();
        this.showCorrespondencesFilter = false;
    }


    @computedFrom('CorrespondencesFilterIds', 'statusFilterIds.length')
    public get statusFilterCount() {
        return this.statusFilterIds ? this.statusFilterIds.length : 0;
    }

    rightshipMemberClick(isMobile: boolean = false) {
        this.rightshipMember = !this.rightshipMember;
        if (!isMobile) this.reloadFilters();
    }

    awaitingResponseLT20DaysClick(isMobile: boolean = false) {
        this.awaitingResponseLT20Days = !this.awaitingResponseLT20Days;
        if (!isMobile) this.reloadFilters();
    }


    validityDateModifiedClick(isMobile: boolean = false) {
        this.validityDateModified = !this.validityDateModified;
        if (!isMobile) this.reloadFilters();
    }

    get canAccessCargillOnly() {
        if (!this.userClaimsService.hasPermission(Claims.CanAccessRightShipInspections)) {
            return this.userClaimsService.hasPermission(Claims.CanAccessCargillInspections);
        }
        return false;
    }

    public reloadFilters() {
        const initialFilters = [];

        const statusGuid = Guid.generateGuid();

        if (this.newInspectionFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.NewInspection, "eq", DataType.Number, false, false, statusGuid, true))
        }

        if (this.reviewRequiredFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.ReviewRequired, "eq", DataType.Number, false, false, statusGuid, true));
        }

        if (this.alternativeRequiredFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.AlternativeRequired, "eq", DataType.Number, false, false, statusGuid, true));
        }

        if (this.isPendingVet) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasVet", this.isPendingVet, "eq", DataType.Boolean, false, false, guid, true));
        }

        if (this.canAccessCargillOnly) {
            initialFilters.push(QueryFilter.getQuery("rightShipInspectionTypeId", RightShipInspectionType.CargillSurvey , "eq", DataType.Number, false, false, statusGuid, true));
        }

        if (this.statusFilterIds && this.statusFilterIds.length > 0) {
            const statusIdFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "statusId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: "RS_RightShipInspectionStatuses",
                    isNullable: true,
                },
                filterValue: this.statusFilterIds || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
                groupGuid: statusGuid
            };

            initialFilters.push(statusIdFilter);
        }

        if (this.correspondencesFilterIds && this.correspondencesFilterIds.length > 0) {
            const guid = Guid.generateGuid();
            if (this.correspondencesFilterIds.includes(3)) {
                const day = new Date().setDate(new Date().getDate() - 72);
                initialFilters.push(QueryFilter.getQuery("lastCorrespondenceDate", moment.utc(day).toDate(), "gt", DataType.Date, false, false, guid, false));
            }
            else if (this.correspondencesFilterIds.includes(2)) {
                const day = new Date().setDate(new Date().getDate() - 48);
                initialFilters.push(QueryFilter.getQuery("lastCorrespondenceDate", moment.utc(day).toDate(), "gt", DataType.Date, false, false, guid, false));
            }
            else if (this.correspondencesFilterIds.includes(1)) {
                const day = new Date().setDate(new Date().getDate() - 24);
                initialFilters.push(QueryFilter.getQuery("lastCorrespondenceDate", moment.utc(day).toDate(), "gt", DataType.Date, false, false, guid, false));
            }
            if (this.correspondencesFilterIds.includes(4)) {
                const day = new Date().setDate(new Date().getDate() - 72);
                initialFilters.push(QueryFilter.getQuery("lastCorrespondenceDate", moment.utc(day).toDate(), "le", DataType.Date, false, false, guid, false));
            }
        }

        if(this.closedWithoutGradingFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("numberOfUnConfirmedGrading", 0, "gt", DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery("statusId", [
                RightShipInspectionStatusEnum.Acceptable,
                RightShipInspectionStatusEnum.Unacceptable
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }
        
        if (this.selectedAdditionalInfoName && this.selectedAdditionalInfoName.length > 0) {
            initialFilters.push(QueryFilter.getQuery("additionalInfo", this.selectedAdditionalInfoName, "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.myInspectionsFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("reviewrId", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery("statusId", [
                RightShipInspectionStatusEnum.InspectionRequired,
                RightShipInspectionStatusEnum.NewInspection,
                RightShipInspectionStatusEnum.InProgress,
                RightShipInspectionStatusEnum.AwaitingResponse,
                RightShipInspectionStatusEnum.Scheduled,
                RightShipInspectionStatusEnum.ReviewRequired,
                RightShipInspectionStatusEnum.RightShipInspectionReassignment,
                RightShipInspectionStatusEnum.Booked,
                RightShipInspectionStatusEnum.AlternativeRequired,
                RightShipInspectionStatusEnum.InspectorAssigned
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.buFilterActive && !this.rightShipUser) {

            const buFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "businessUnitId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedBUs || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(buFilter);
        }

        if (this.customerFilterActive && this.rightShipUser) {

            const customerFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "customerId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCustomers || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(customerFilter);
        }

        if (this.inspectionTypeFilterActive) {

            const inspectionTypeFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "rightShipInspectionTypeId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedInspectionTypes || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(inspectionTypeFilter);
        }

        if (this.billingStatusFilterActive) {

            const billingStatusesFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "billingStatusId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedBillingStatuses || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(billingStatusesFilter);
        }

        if (this.selectedPorts) {
            const portFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "portId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedPorts || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(portFilter);
        }
        if (this.selectedInspectors) {
            const inspectorFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "inspectorId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedInspectors || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(inspectorFilter);
        }



        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            initialFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            if (!isNaN(parseInt(this.appliedFilter)))
                initialFilters.push(QueryFilter.getQuery("rightShipInspectionId", this.appliedFilter, "{0} eq {1}", DataType.Number, false, false, guid, true, true));
        }

        if (this.rightshipMember) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
        }

        if(this.awaitingResponseLT20Days) {
            const guid = Guid.generateGuid();
            const day = new Date().setDate(new Date().getDate() - 20);
            initialFilters.push(QueryFilter.getQuery("rightShipInspectionTypeId", RightShipInspectionType.RightShipInspection, "eq", DataType.Number, true, false, guid, false));
            initialFilters.push(QueryFilter.getQuery("statusId", RightShipInspectionStatusEnum.AwaitingResponse, "eq", DataType.Number, true, false, guid, false));
            initialFilters.push(QueryFilter.getQuery("statusDate", moment.utc(day).toDate(), "lt", DataType.Date, true, false, guid, false));
        }

        if (this.validityDateModified) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("validityDateModified", true, "eq", DataType.Boolean, true, false, guid, true));
        }

        if(this.unreadMessageFilterSelected) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("unreadMessage", 0, "gt", DataType.Number, true, false, guid, true));
        }

        if (this.isPendingDueDiligence && this.dueDiligenceFilterVisible) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasDueDiligence", this.isPendingDueDiligence, "eq", DataType.Boolean, true, false, guid, true));
        }

        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: initialFilters,
            initialSorting: this.initialSorting
        };

        this.refreshActivityCounts();
    }

    openMobileStatus() {
        this.mobileStatusOpen = true;
    }

    closedMobileStatus() {
        this.mobileStatusOpen = false;
    }

    private mobileStatusIds: number[] = [];

    mobileStatusClick(value: LookupValue) {
        const existingIndex = this.mobileStatusIds.indexOf(value.id);

        if (existingIndex > -1) {
            this.mobileStatusIds.splice(existingIndex, 1);
        } else {
            this.mobileStatusIds.push(value.id);
        }

        this.statusFilterIds = this.mobileStatusIds;

        this.bindingSignaler.signal('mobileStatusChange');
    }

    public isMobileStatusSelected(id: number) {
        return this.mobileStatusIds.indexOf(id) > -1;
    }

    public onMobileApply() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
            this.closeAllFilters();
        }

        if (this.mobileStatusOpen) {
            this.mobileStatusOpen = false;
        } else {
            this.reloadFilters();
            this.toggleMobileFilterDialog();
        }
    }

    clearAllMobile() {
        this.statusFilterIds = [];
        this.mobileStatusIds = [];
        this.myInspectionsFilterSelected = false;
        this.newInspectionFilterSelected = false;
        this.alternativeRequiredFilterSelected = false;
        this.reviewRequiredFilterSelected = false;
        this.closedWithoutGradingFilterSelected = false;
        this.selectedBUs = [];
        this.selectedCustomers = [];
        this.selectedInspectionTypes = [];
        this.rightshipMember = false;
        this.validityDateModified = false;
        this.isPendingVet = false;
        this.isPendingDueDiligence = false;
        this.selectedAdditionalInfo = [];
        this.unreadMessageFilterSelected = false;
        this.bindingSignaler.signal('mobileStatusChange');
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showBUFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showCustomerFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showInspectionTypeFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showBillingStatusFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showCorrespondencesFilter),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.showAdditionalInfoFilter))
    public get mobileFilterSelected() {
        return this.showCustomerFilter || this.showBUFilter || this.showInspectionTypeFilter || this.showAdditionalInfoFilter || this.showBillingStatusFilter || this.showCorrespondencesFilter;
    }

    public newInspectionCount: number = 0;
    public reviewRequiredCount: number = 0;
    public myInspectionsCount: number = 0;
    public alternativeRequiredCount: number = 0;

    refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessRightShipInspections)) {
            this.queryService.getData(`${this.odataUrl}/Genesis.GetStatusCounts()`).then(x => {

                this.clearCounts();

                for (let i = 0; i < x.keys.length; i++) {

                    const key = x.keys[i];
                    const value = x.values[i];

                    if (key == RightShipInspectionStatusEnum.NewInspection) {
                        this.newInspectionCount = value;
                    } else if (key == RightShipInspectionStatusEnum.ReviewRequired) {
                        this.reviewRequiredCount = value;
                    } else if (key == RightShipInspectionStatusEnum.AlternativeRequired) {
                        this.alternativeRequiredCount = value;
                    } else if (key == -1) {
                        this.myInspectionsCount = value;
                    } else if (key == -2) {
                        this.pendingVetCount = value;
                    } else if (key == -3) {
                        this.pendingDueDiligenceCount = value;
                    }
                }
            });
        }
    }

    clearCounts() {
        this.newInspectionCount = 0;
        this.reviewRequiredCount = 0;
        this.alternativeRequiredCount = 0;
        this.myInspectionsCount = 0;
        this.pendingVetCount = 0;
        this.pendingDueDiligenceCount = 0;
    }

    @cachable
    public myInspectionsFilterSelected: boolean = false;

    myInspectionsClick(isMobile: boolean = false) {
        this.myInspectionsFilterSelected = !this.myInspectionsFilterSelected;
        if (!isMobile) this.reloadFilters();
    }

    @cachable
    public isPendingVet: boolean = false;

    pendingVetClick(isMobile: boolean = false) {
        this.isPendingVet = !this.isPendingVet;
        if (!isMobile) this.reloadFilters();
    }

    public closeAllFilters() {
        this.showCustomerFilter = false;
        this.showStatusFilter = false;
        this.showBUFilter = false;
        this.showInspectionTypeFilter = false;
        this.showAdditionalInfoFilter = false
        this.showBillingStatusFilter = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    //#region Billing Status filter
    public showBillingStatusFilter: boolean = false;
    @cachable
    public selectedBillingStatuses: number[] = [];

    public toggleBillingStatusFilters() {
        if (!this.showBillingStatusFilter) {
            this.closeAllFilters();
        }
        this.showBillingStatusFilter = !this.showBillingStatusFilter;
        this.selectedFilter = {
            title: "Billing",
            clear: () => {
                this.clearBillingStatusFilter();
            },
            apply: () => {
                this.applyBillingStatusFilter(this.selectedBillingStatuses.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyBillingStatusFilter(items: any[], reload: boolean = true) {
        this.selectedBillingStatuses = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleBillingStatusFilters();
    }

    public clearBillingStatusFilter() {
        this.selectedBillingStatuses = [];
        this.reloadFilters();
    }

    @computedFrom("selectedBillingStatuses", "selectedBillingStatuses.length")
    public get billingStatusFilterActive() {
        return this.selectedBillingStatuses && this.selectedBillingStatuses.length > 0;
    }

    //#region BU filter
    public showBUFilter: boolean = false;
    @cachable
    public selectedBUs: number[] = [];

    public toggleBUFilters() {
        if (!this.showBUFilter) {
            this.closeAllFilters();
        }
        this.showBUFilter = !this.showBUFilter;
        this.selectedFilter = {
            title: "Business Unit",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyBUFilter(this.selectedBUs.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyBUFilter(items: any[], reload: boolean = true) {
        this.selectedBUs = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleBUFilters();
    }

    public clearBUFilter() {
        this.selectedBUs = [];
        this.reloadFilters();
    }

    @computedFrom("selectedBUs", "selectedBUs.length")
    public get buFilterActive() {
        return this.selectedBUs && this.selectedBUs.length > 0;
    }

    //#endregion

    //#region Customer filter
    public showCustomerFilter: boolean = false;
    @cachable
    public selectedCustomers: number[] = [];

    public toggleCustomerFilters() {
        if (!this.showCustomerFilter) {
            this.closeAllFilters();
        }
        this.showCustomerFilter = !this.showCustomerFilter;
        this.selectedFilter = {
            title: "Customer",
            clear: () => {
                this.clearCustomerFilter();
            },
            apply: () => {
                this.applyCustomerFilter(this.selectedCustomers.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyCustomerFilter(items: any[], reload: boolean = true) {
        this.selectedCustomers = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleCustomerFilters();
    }

    public clearCustomerFilter() {
        this.selectedCustomers = [];
        this.reloadFilters();
    }

    @computedFrom("selectedCustomers", "selectedCustomers.length")
    public get customerFilterActive() {
        return this.selectedCustomers && this.selectedCustomers.length > 0;
    }
    //#endregion

    //#region Unread message filter    
    @cachable
    public unreadMessageFilterSelected: boolean = false;
    unreadMessageClick() {
        this.unreadMessageFilterSelected = !this.unreadMessageFilterSelected;
        this.reloadFilters();
    }
    //#endregion

    //#region RS Inspection types filter
    public showInspectionTypeFilter: boolean = false;
    @cachable
    public selectedInspectionTypes: number[] = [];

    public toggleInspectionTypeFilters() {
        if (!this.showInspectionTypeFilter) {
            this.closeAllFilters();
        }
        this.showInspectionTypeFilter = !this.showInspectionTypeFilter;
        this.selectedFilter = {
            title: "Vet Outcome",
            clear: () => {
                this.clearInspectionTypeFilter();
            },
            apply: () => {
                this.applyInspectionTypeFilter(this.selectedInspectionTypes.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyInspectionTypeFilter(items: any[], reload: boolean = true) {
        this.selectedInspectionTypes = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleInspectionTypeFilters();
    }

    public clearInspectionTypeFilter() {
        this.selectedInspectionTypes = [];
        this.reloadFilters();
    }

    @computedFrom("selectedInspectionTypes", "selectedInspectionTypes.length")
    public get inspectionTypeFilterActive() {
        return this.selectedInspectionTypes && this.selectedInspectionTypes.length > 0;
    }

    public static getDateToString(date: Date | string): string {
        if (!date)
            return ''

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';
        return moment(val).format('MMMM Do YYYY');
    }

    //#endregion

    //#region RS Port filter
    public portEntityFinderFilter: EntityFinderSearchFilter;
    public showPortFilter: boolean = false;
    @cachable
    public selectedPorts: number[] = [];
    public clearPortOnShow: boolean;

    public togglePortFilters() {
        if (!this.showPortFilter) {
            this.closeAllFilters();
        }
        this.showPortFilter = !this.showPortFilter;
        this.selectedFilter = {
            title: "Ports",
            clear: () => {
                this.clearPortFilter();
            },
            apply: () => {
                this.applyPortFilter(this.selectedPorts.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyPortFilter(items: any[], reload: boolean = true) {
        this.selectedPorts = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.togglePortFilters();
    }

    public clearPortFilter() {
        this.selectedPorts = [];
        if (this.portEntityFinderFilter) {
            this.portEntityFinderFilter.clear();
        }
        else {
            this.clearPortOnShow = true;
        }
        this.reloadFilters();
    }

    @computedFrom("selectedPorts", "selectedPorts.length")
    public get portFilterActive() {
        return this.selectedPorts && this.selectedPorts.length > 0;
    }
    //#endregion

    //#region Inspector filter
    public inspectorEntityFinderFilter: EntityFinderSearchFilter;
    public showInspectorFilter: boolean = false;
    @cachable
    public selectedInspectors: number[] = [];
    public clearInspectorOnShow: boolean;

    public toggleInspectorFilters() {
        if (!this.showInspectorFilter) {
            this.closeAllFilters();
        }
        this.showInspectorFilter = !this.showInspectorFilter;
        this.selectedFilter = {
            title: "Inspectors",
            clear: () => {
                this.clearInspectorFilter();
            },
            apply: () => {
                this.applyInspectorFilter(this.selectedInspectors.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyInspectorFilter(items: any[], reload: boolean = true) {
        this.selectedInspectors = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleInspectorFilters();
    }

    public clearInspectorFilter() {
        this.selectedInspectors = [];
        if (this.inspectorEntityFinderFilter) {
            this.inspectorEntityFinderFilter.clear();
        }
        else {
            this.clearInspectorOnShow = true;
        }
        this.reloadFilters();
    }

    @computedFrom("selectedInspectors", "selectedInspectors.length")
    public get inspectorFilterActive() {
        return this.selectedInspectors && this.selectedInspectors.length > 0;
    }
    //#endregion


    //#region Additional Info filter
    public showAdditionalInfoFilter: boolean = false;
    public selectedAdditionalInfo: number[] = [];

    public toggleAdditionalInfoFilters() {
        if (!this.showAdditionalInfoFilter) {
            this.closeAllFilters();
        }
        this.showAdditionalInfoFilter = !this.showAdditionalInfoFilter;
    }

    public applyAdditionalInfoFilter(items: any[], reload: boolean = true) {
        this.selectedAdditionalInfo = items.map(x => x.id);
        this.selectedAdditionalInfoName = this.additionalInfos.filter(item => this.selectedAdditionalInfo.find(id => id == item.id)).map(result => result.dbpropertyValue);
        if (reload == true) this.reloadFilters();
        this.toggleAdditionalInfoFilters();
    }

    public clearAdditionalInfoFilter() {
        this.selectedAdditionalInfo = [];
        this.reloadFilters();
    }

    @computedFrom("selectedAdditionalInfo", "selectedAdditionalInfo.length")
    public get additionalInfoFilterActive() {
        return this.selectedAdditionalInfo && this.selectedAdditionalInfo.length > 0;
    }

    //#endregion

    //#region due diligence filter
    
    public pendingDueDiligenceCount: number = 0;
    public canAccessDueDiligence: boolean;
    @cachable
    public isPendingDueDiligence: boolean;
    
    @computedFrom(Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.canAccessDueDiligence),
        Reflection.getPropertyPath<RightShipInspectionActivities>(z => z.rightShipUser))
    public get dueDiligenceFilterVisible(): boolean
    {
        return this.canAccessDueDiligence && this.rightShipUser;
    }

    pendingDueDiligenceClick(isMobile: boolean = false) {
        this.isPendingDueDiligence = !this.isPendingDueDiligence;
        if (!isMobile) this.reloadFilters();
    }

    //#endregion
}
